import * as Dialog from '@radix-ui/react-dialog';
import { Camera, ImageUp } from 'lucide-react';
import { useState, type FormEvent } from 'react';

import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/field';
import { useToast } from '@/lib/toast-context';
import { enqueue } from '@/lib/upload-queue';
import type { EmployeeJobTaskView } from '@shared/schemas/employee-portal';

/*
 * The crew's own capture flow — ROADMAP.md §6.4.
 *
 * Two differences from the office's `PhotoUploadDialog`, and both are the point.
 *
 *   - **No "taken by" field.** The office attaches photos on a cleaner's behalf
 *     and has to say whose evidence it is; a cleaner is already identified by
 *     their session, and the endpoint has no field to put another person's id
 *     into.
 *   - **The camera is the first option, not a file picker that happens to allow
 *     it.** `capture="environment"` asks a phone for the rear camera directly.
 *     A second input without it is kept for choosing a photo already taken —
 *     because "I photographed it before my hands were dirty" is a real thing
 *     that happens, and a flow that cannot accept it is a flow people work
 *     around.
 *
 * The task is fixed by whichever Add photo button opened this, so there is no
 * task picker to get wrong. The kind defaults to After, which is what nine of
 * ten photos on a cleaning job are.
 *
 * **The caller mounts this only while it is open, keyed by task.** That is what
 * makes reopening for a different task start clean, rather than an effect that
 * resets four pieces of state — a fresh mount is a fresh `useState`, and it
 * cannot forget one of them.
 */

const KINDS = [
  { value: 'after', label: 'After' },
  { value: 'before', label: 'Before' },
  { value: 'issue', label: 'Issue' },
] as const;

export function PhotoCaptureSheet({
  jobId,
  task,
  open,
  onOpenChange,
}: {
  jobId: string;
  /** The task this photo completes, or null for a photo about the job itself. */
  task: EmployeeJobTaskView | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const toast = useToast();

  const [file, setFile] = useState<File | null>(null);
  const [kind, setKind] = useState<'before' | 'after' | 'issue'>('after');
  const [caption, setCaption] = useState('');

  const completesTask = task !== null && task.requiresPhoto && !task.isCompleted;

  /*
   * **Queued, not uploaded.** The sheet closes the moment the photo is handed
   * over and the queue does the work in the background, retrying through a bad
   * signal (`lib/upload-queue.ts`). That is the difference between a cleaner
   * waiting on a spinner in a basement and one carrying on with the next room.
   *
   * The task does **not** go green here. It completes when the server says so,
   * because the server is what completes it (business-rules.md §3.1) — until
   * then the checklist row says the photo is waiting to upload, which is the
   * truth.
   */
  function submit(event: FormEvent) {
    event.preventDefault();
    if (!file) return;

    enqueue({
      jobId,
      jobTaskId: task?.id ?? null,
      taskName: task?.name ?? null,
      kind,
      caption: caption.trim() === '' ? null : caption.trim(),
      file,
    });

    toast.success(
      completesTask
        ? 'Photo added — it will complete the task once it uploads.'
        : 'Photo added — uploading in the background.',
    );
    onOpenChange(false);
  }

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/40" />
        {/*
          A sheet from the bottom on a phone, a centred dialog from `sm` up. The
          bottom is where a thumb is, and this is opened one-handed.
        */}
        <Dialog.Content className="fixed inset-x-0 bottom-0 z-50 max-h-[calc(100vh-3rem)] overflow-y-auto rounded-t-lg border border-ink-300 bg-white p-5 shadow-pop sm:inset-x-auto sm:bottom-auto sm:left-1/2 sm:top-1/2 sm:w-[calc(100vw-2rem)] sm:max-w-lg sm:-translate-x-1/2 sm:-translate-y-1/2 sm:rounded-lg sm:p-6">
          <Dialog.Title className="text-h2 text-ink-900">
            {task === null ? 'Add a photo' : task.name}
          </Dialog.Title>
          <Dialog.Description className="mt-1 text-body text-ink-500">
            {completesTask
              ? 'This photo completes the task once it uploads.'
              : 'GPS and other camera data are stripped on the way in.'}
          </Dialog.Description>

          {/*
            No error region here, deliberately. Nothing can fail at this point —
            the photo is handed to the queue and the queue is what reports a
            refusal, on the job screen, where it can still be retried.
          */}
          <form onSubmit={submit} className="mt-5 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="capture-photo" required>
                Photo
              </Label>

              {/*
                Two inputs, one purpose. The label is styled as the button and
                the input itself is visually hidden but focusable — so the tap
                target is the whole 44px-tall label rather than a browser's own
                small "Choose file" control.
              */}
              <div className="grid gap-2 sm:grid-cols-2">
                <CaptureInput
                  id="capture-photo"
                  capture
                  icon={Camera}
                  label="Take a photo"
                  onFile={setFile}
                />
                <CaptureInput
                  id="choose-photo"
                  icon={ImageUp}
                  label="Choose a photo"
                  onFile={setFile}
                />
              </div>

              <p className="text-meta text-ink-500" aria-live="polite">
                {file === null ? 'JPEG, PNG, WebP or HEIC, up to 10MB.' : `Chosen: ${file.name}`}
              </p>
            </div>

            <fieldset className="space-y-2">
              <legend className="text-label text-ink-900">What kind of photo</legend>
              {/*
                `focus-within` again, and for a subtler reason than the file
                inputs above. These radios are `sr-only`, so their focus ring is
                painted onto a clipped box; what a keyboard user sees is the
                pill. Arrow keys through a radio group move selection with
                focus, so the yellow background does track focus and it is
                tempting to call that enough — but **Tab into the group from
                outside lands on the checked radio and changes nothing**, and a
                background that means "selected" is not an indicator of where
                focus is. Two different things were sharing one signal.
              */}
              <div className="flex flex-wrap gap-2">
                {KINDS.map((option) => (
                  <label
                    key={option.value}
                    className={
                      kind === option.value
                        ? 'inline-flex min-h-touch cursor-pointer items-center rounded-full bg-honey-400 px-4 text-label text-ink-900 focus-within:outline focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-ink-900'
                        : 'inline-flex min-h-touch cursor-pointer items-center rounded-full border border-ink-300 px-4 text-label text-ink-700 focus-within:outline focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-ink-900 hover:bg-honey-50'
                    }
                  >
                    <input
                      type="radio"
                      name="kind"
                      value={option.value}
                      checked={kind === option.value}
                      onChange={() => {
                        setKind(option.value);
                      }}
                      className="sr-only"
                    />
                    {option.label}
                  </label>
                ))}
              </div>
            </fieldset>

            <div className="space-y-1.5">
              <Label htmlFor="capture-caption">Note (optional)</Label>
              <input
                id="capture-caption"
                type="text"
                value={caption}
                maxLength={512}
                onChange={(event) => {
                  setCaption(event.target.value);
                }}
                placeholder="Anything the office should know"
                className="min-h-control w-full rounded-sm border border-ink-300 bg-white px-3 text-body"
              />
            </div>

            <div className="flex flex-col-reverse gap-2 pt-1 sm:flex-row sm:justify-end">
              <Button
                type="button"
                variant="outline"
                full
                onClick={() => {
                  onOpenChange(false);
                }}
                className="sm:w-auto"
              >
                Cancel
              </Button>
              <Button type="submit" full disabled={file === null}>
                Save photo
              </Button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/**
 * A file input dressed as a 44px button.
 *
 * `capture="environment"` is what makes a phone open the rear camera rather
 * than the photo library. It is ignored on desktop, which is exactly the
 * behaviour wanted — the same control works in both places.
 */
function CaptureInput({
  id,
  capture,
  icon: Icon,
  label,
  onFile,
}: {
  id: string;
  capture?: boolean;
  icon: typeof Camera;
  label: string;
  onFile: (file: File | null) => void;
}) {
  return (
    /*
     * **`focus-within` is the focus ring.** The input is `sr-only`, so the
     * global `:focus-visible` rule paints its outline onto a clipped box and a
     * keyboard user sees nothing — the same defect 10.1 found on the reply box.
     *
     * It matters more here than anywhere: this is the crew's Add photo control,
     * pressed ten times a job, and uploading the first photo to a photo-required
     * task **is** the completion event (business-rules.md §3.1). The sweeps do
     * not reach it, because it lives inside a sheet that only opens after an
     * interaction — so it was fixed by grepping for the shape rather than by
     * being caught.
     */
    <label
      htmlFor={id}
      className="inline-flex min-h-touch cursor-pointer items-center justify-center gap-2 rounded-md border border-bark-700 bg-white px-4 text-label font-medium text-bark-700 focus-within:outline focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-ink-900 hover:bg-honey-50"
    >
      <Icon aria-hidden="true" className="size-[18px] shrink-0" />
      {label}
      <input
        id={id}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
        {...(capture === true ? { capture: 'environment' as const } : {})}
        onChange={(event) => {
          onFile(event.target.files?.[0] ?? null);
        }}
        className="sr-only"
      />
    </label>
  );
}
